import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Spinner,
  Alert,
  ListGroup,
  Row,
  Col,
} from "react-bootstrap";
import { toast } from "react-toastify";
import { apiGet, apiDelete } from "../../api/api";
import { fetchFileSize } from "../../utils/fileUtils";

const AttachmentsModal = ({
  show,
  onHide,
  taskId,
  officerName = "Officer",
  officerIds,
  onFileDelete,
  roleName,
  userId,
  attachmentCount, // Add this new prop
}) => {
  const [attachments, setAttachments] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileSizes, setFileSizes] = useState({});
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    fileId: null,
    fileName: "",
  });

  useEffect(() => {
    const loadFileSizes = async () => {
      const sizes = {};
      console.log("called");
      await Promise.all(
        attachments.map(async (file) => {
          const size = await fetchFileSize(
            `${process.env.REACT_APP_API_BASE_URL}/uploads/tasks/TaskUpload/${file.fileName}`
          );
          console.log(size);
          if (size !== null) {
            sizes[file.attachmentID] = size;
          }
        })
      );
      setFileSizes((prev) => ({ ...prev, ...sizes }));
    };

    loadFileSizes();
  }, [attachments]);

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return "0 KB";
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(2)} KB`;
    const mb = kb / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  const getFileIcon = (fileName) => {
    if (fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
      return <i className="bi bi-image-fill text-btn-primary" />;
    }
    if (fileName.match(/\.pdf$/i)) {
      return <i className="bi bi-file-earmark-pdf-fill text-danger" />;
    }
    if (fileName.match(/\.(doc|docx)$/i)) {
      return <i className="bi bi-file-earmark-word-fill text-btn-primary" />;
    }
    return <i className="bi bi-file-earmark-fill" />;
  };

const renderFilePreview = (file) => {
  if (!file) return null;

  return (
    <div className="text-center">
      {file.fileName.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
        <img
          src={file.url}
          alt={file.fileName}
          className="img-fluid rounded"
          style={{ maxHeight: "400px", maxWidth: "100%" }}
        />
      ) : file.fileName.match(/\.pdf$/i) ? (
        <iframe
          src={file.url}
          title={file.fileName}
          width="100%"
          height="400px"
          className="border-0"
          style={{ minHeight: "400px" }}
        />
      ) : (
        <div className="py-5">
          <i className="bi bi-file-earmark-text fs-1 text-muted" />
          <p className="mt-2">Preview not available for this file type</p>
        </div>
      )}
      
      
      {file.username && (
        <div className="mt-3 p-2 bg-light rounded">
          <small className="text-muted">Uploaded by:</small>
          <div className="fw-bold">{file.username}</div>
        </div>
      )}
    </div>
  );
};
  

const downloadFile = async (file) => {
  if (!file) return;
  try {
    const response = await fetch(file.url, {
      method: "GET",
    });
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = file.fileName; 
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Download failed:", error);
    toast.error("Failed to download file");
  }
};


  const handleClose = () => {
    setPreviewFile(null);
    onHide();
  };

  const confirmDelete = (fileId, fileName) => {
    setDeleteConfirmation({
      show: true,
      fileId,
      fileName,
    });
  };

  const handleDeleteFile = async () => {
    if (!deleteConfirmation.fileId || !taskId) return;
    try {
      setLoading(true);
      const response = await apiDelete(
        `/TaskTracking/DeleteAttachment/${deleteConfirmation.fileId}`
      );
      if (response?.isSuccess) {
        toast.success("File deleted successfully");
        const updatedAttachments = attachments.filter(
          (file) => file.attachmentID !== deleteConfirmation.fileId
        );
        setAttachments(updatedAttachments);

        // Update preview file if we're deleting the currently previewed file
        if (previewFile?.attachmentID === deleteConfirmation.fileId) {
          setPreviewFile(
            updatedAttachments.length > 0 ? updatedAttachments[0] : null
          );
        }

        // Notify parent with the new count of attachments
        if (onFileDelete) onFileDelete(updatedAttachments.length);
      } else {
        throw new Error(response?.message || "Failed to delete file");
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      toast.error(error.message || "Failed to delete file");
    } finally {
      setDeleteConfirmation({ show: false, fileId: null, fileName: "" });
      setLoading(false);
    }
  };

  const fetchAttachments = async () => {
    if (!taskId) return;
    try {
      setLoading(true);
      setError(null);
      setPreviewFile(null);
      const response = await apiGet(`/TaskTracking/GetAttachments/${taskId}`);
      if (response?.data) {
        const formattedAttachments = response.data.map((attachment) => ({
          ...attachment,
          url: `${process.env.REACT_APP_API_BASE_URL}/uploads/tasks/TaskUpload/${attachment.fileName}`,
          fileSize: attachment.fileSize || 0,
        }));
        setAttachments(formattedAttachments);
        if (formattedAttachments.length > 0) {
          setPreviewFile(formattedAttachments[0]);
        }
        // Notify parent with the initial count
        if (onFileDelete) onFileDelete(formattedAttachments.length);
      } else {
        setAttachments([]);
        if (onFileDelete) onFileDelete(0);
        toast.error("No attachments found");
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      setError(error.response?.data?.message || "Failed to load attachments");
      toast.error("Failed to load attachments");
      if (onFileDelete) onFileDelete(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && taskId) {
      fetchAttachments();
    }
  }, [show, taskId]);

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered>
        <Modal.Header
          closeButton
      className="modal-customheader"
          closeVariant=""
        >
          <Modal.Title className="custom-modaltitle"> 
            <h5 className="mb-0">
             Attachments{taskId && ` (TaskID ${taskId})`}
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body  className="custommodal-body">
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="btn-primary" />
              <p className="mt-2">Loading attachments...</p>
            </div>
          ) : error ? (
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle-fill me-2" />
              {error}
            </Alert>
          ) : attachments.length > 0 ? (
            <Row>
              <Col md={4}>
                <ListGroup
                  className="mb-3"
                  style={{ maxHeight: "500px", overflowY: "auto" }}
                >
                  {attachments.map((file, index) => (
                    <ListGroup.Item
                      key={file.attachmentID || index}
                      action
                      active={previewFile?.attachmentID === file.attachmentID}
                      onClick={() => setPreviewFile(file)}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <div className="d-flex align-items-center">
                        {getFileIcon(file.fileName)}
                        <span
                          className="ms-2 text-truncate"
                          style={{ maxWidth: "200px" }}
                        >
                          {file.fileName}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <small className="text-muted me-2">
                          {formatFileSize(
                            file.fileSize !== 0
                              ? file.fileSize
                              : fileSizes[file.attachmentID]
                          )}
                        </small>
                        {((roleName === "TaskCoordinator" &&
                          (file.createdByID === userId ||
                            officerIds
                              ?.split(",")
                              .map((id) => parseInt(id.trim()))
                              .find((i) => i === file.createdByID))) ||
                          roleName === "SuperAdmin" ||
                          roleName === "Admin") && (
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmDelete(file.attachmentID, file.fileName);
                            }}
                            title="Delete file"
                          >
                            <i className="bi bi-trash" />
                          </Button>
                        )}
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Col>
              <Col md={8}>
                <div className="border rounded p-3 h-100 d-flex flex-column custombox">
                  <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-light rounded mb-3">
                    {renderFilePreview(previewFile)}
                  </div>
                  <div className="d-flex justify-content-lg-end justify-content-center  gap-2">
                    <Button
                        className="btn-fontweight smallcancelbtn-fontsize"
                      variant="secondary"
                      onClick={() => window.open(previewFile?.url, "_blank")}
                      disabled={!previewFile}
                    >
                      <i className="bi bi-box-arrow-up-right me-1" /> Open
                    </Button>
                    <Button
                      variant="btn-primary"
                          className="btn-fontweight addbtn-fontsize"
                      onClick={() => downloadFile(previewFile)}
                      disabled={!previewFile}
                    >
                      <i className="bi bi-download me-1" /> Download
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          ) : (
            <div className="text-center py-4">
              <i className="bi bi-folder-x fs-1 text-muted" />
              <p className="mt-2">No attachments available</p>
            </div>
          )}
        </Modal.Body>
        {/* <Modal.Footer>
          <Button variant="btn btn-outline-btn-primary" onClick={handleClose}>
              <i className="bi bi-x-lg me-2" />
            Close
          </Button>
        </Modal.Footer> */}
      </Modal>

      <Modal
        show={deleteConfirmation.show}
        onHide={() =>
          setDeleteConfirmation({ show: false, fileId: null, fileName: "" })
        }
        centered
        backdrop="static"
      >
        <Modal.Header closeButton className="modal-customheaderdelete modal-header">
          <Modal.Title className="custom-modaltitle">Are you sure you want to delete?{" "}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="confirmdeletion-body">
      
          <div className="image-div">{deleteConfirmation.fileName}</div>?
          <br />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
             className="btn-fontweight addbtn-fontsize"
            onClick={() =>
              setDeleteConfirmation({ show: false, fileId: null, fileName: "" })
            }
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="btn-fontweight addbtn-fontsize btn btn-btn-primary"
            onClick={handleDeleteFile}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AttachmentsModal;
